/**
 * Chat summary query operations - Create
 */

import { v4 as uuidv4 } from 'uuid';
import { getSupabaseClient } from '../../supabase-client.js';
import type { ChatSummary } from '../types.js';
import { logger } from '../../../logger.js';
import { mapChatSummaryRow, type ChatSummaryRow } from './row-mapper.js';

/**
 * Fields accepted when creating a summary row.
 *
 * The rolling-compaction fields are OPTIONAL: a row is also created empty at chat
 * creation time (see the entity-chat route), long before there is anything to
 * compact. Requiring callers to pass empty arrays and a zero count there would be
 * noise, so the column defaults live here instead.
 */
export type CreateChatSummaryInput = Omit<
  ChatSummary,
  'id' | 'createdAt' | 'updatedAt' | 'keyDecisions' | 'keyEntities' | 'openThreads' | 'summarizedThroughCount'
> &
  Partial<
    Pick<ChatSummary, 'keyDecisions' | 'keyEntities' | 'openThreads' | 'summarizedThroughCount'>
  >;

/**
 * Create a new chat summary
 */
export async function createChatSummary(
  summary: CreateChatSummaryInput
): Promise<ChatSummary> {
  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();
    const id = uuidv4();

    const { data, error } = await client
      .from('meridian_chat_summaries')
      .insert({
        id,
        chat_id: summary.chatId,
        user_id: summary.userId,
        summary: summary.summary,
        key_topics: summary.keyTopics || [],
        key_decisions: summary.keyDecisions || [],
        key_entities: summary.keyEntities || [],
        open_threads: summary.openThreads || [],
        summarized_through_count: summary.summarizedThroughCount ?? 0,
        created_at: now,
        updated_at: now,
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return mapChatSummaryRow(data as ChatSummaryRow);
  } catch (error) {
    logger.error('error creating chat summary', error, { op: 'createChatSummary' });
    throw error;
  }
}
